import React, { useMemo, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Box, Hint, PreFormattedHtmlMessage, RoundDoneIcon, Text, useResponsive } from '@common-fe/common-fe';
import { uniqueId } from 'lodash';
import styled from 'styled-components';

import ROUTES from '@/common/routes';
import StatusIcon from '@/components/elements/StatusIcon';
import { useHistory } from '@/modules/core/hooks';
import {
  BorderStyleType,
  PreFormattedData,
  SectionStyleType,
  TransactionData,
  TransactionStatus,
} from '@/modules/transaction/transaction.types';
import { PlaidDirectDepositBanner } from '@/modules/user/components/ManageProfile/components/ReimbursementOptions/components/PaymentMethodsList/PlaidDirectDepositBanner';
import theme from '@/styles/theme';

import { usePlaidAccountVerificationStatusStore } from '../../../TransactionPaymentDetails/stores';
import { ClaimAppealModal } from '../../ClaimAppealModal';
import { IconWrapper, Wrapper } from '../../TransactionNotification.styles';
import ActionRequiredMessage from '../ActionRequiredMessage/ActionRequiredMessage';
import { getClaimRepaymentMessageType } from '../ClaimRepaymentMessage/ClaimRepaymentMessage';
import ClaimRepaymentModal from '../ClaimRepaymentMessage/ClaimRepaymentModal';

const getBorderColor = (style?: SectionStyleType) => {
  switch (style) {
    case SectionStyleType.SUCCESS:
      return theme.colors.successBorder;
    case SectionStyleType.ERROR:
      return theme.colors.dangerBorder;
    case SectionStyleType.WARNING:
      return theme.colors.warningBorder;
    case SectionStyleType.INFO:
      return theme.colors.infoBorder;
    case SectionStyleType.DEFAULT:
      return theme.colors.border2;
    default:
      return theme.colors.border2;
  }
};

const APPEAL_SUBMITTED_ICON = 'appeal-submitted';

const PreformattedWrapper = styled(Box)`
  > div,
  span {
    font-size: 16px;
    line-height: 28px;
    b {
      font-weight: bold;
    }
    ol,
    ul {
      font-size: 14px;
      line-height: 20px;
      margin-top: 8px;
    }
    ol {
      margin-left: 18px;
      li {
        list-style: auto;
      }
    }
    ul li {
      list-style: inside;
    }
  }
`;

interface Props {
  preFormattedData?: PreFormattedData;
  onUploadDocument?: () => void;
  onUploadLmnDocument?: () => void;
  data?: TransactionData;
  isUploadButtonEnabled?: boolean;
}

const PreFormattedMessage: React.FC<Props> = ({
  preFormattedData,
  onUploadDocument,
  onUploadLmnDocument,
  data,
  isUploadButtonEnabled,
}) => {
  const { push } = useHistory();
  const [isChoosingPaymentMethod, setIsChoosingPaymentMethod] = useState(false);
  const [onlyOpenPlaidModeId, setOnlyOpenPlaidModeId] = useState('');
  const [isRepaymentShown, setIsRepaymentShown] = useState(false);
  const [isAppealOpen, setIsAppealOpen] = useState(false);
  const claimRepaymentMessageType = getClaimRepaymentMessageType(data);
  const [
    isSuccessStyle,
    isErrorStyle,
    isWarningStyle,
    isInfoSectionStyle,
    isDefaultStyle,
    isInfoStyle,
    borderColor,
  ] = useMemo(
    () => [
      preFormattedData?.sectionStyleType === SectionStyleType.SUCCESS,
      preFormattedData?.sectionStyleType === SectionStyleType.ERROR,
      preFormattedData?.sectionStyleType === SectionStyleType.WARNING,
      preFormattedData?.sectionStyleType === SectionStyleType.INFO,
      preFormattedData?.sectionStyleType === SectionStyleType.DEFAULT || !preFormattedData?.sectionStyleType,
      preFormattedData?.borderStyleType === BorderStyleType.INFO,
      getBorderColor(preFormattedData?.sectionStyleType),
    ],
    [preFormattedData?.sectionStyleType, preFormattedData?.borderStyleType]
  );

  const processedHtmlText = useMemo(() => {
    const html = preFormattedData?.htmlText;
    if (!html?.includes(APPEAL_SUBMITTED_ICON)) return html;
    const iconHtml = renderToStaticMarkup(
      <RoundDoneIcon
        color={theme.colors.buttonPrimary}
        size="20px"
        style={{ display: 'inline-block', verticalAlign: 'text-bottom', marginRight: '8px'}}
      />
    );
    return html.replace(/(data-icon=['"]appeal-submitted['"][^>]*)>/, `$1>${iconHtml}`);
  }, [preFormattedData?.htmlText]);

  const plaidAccountVerificationStatus = usePlaidAccountVerificationStatusStore(
    (state) => state.plaidAccountVerificationStatus
  );
  const plaidAccountId = usePlaidAccountVerificationStatusStore((state) => state.accountId);
  const additionalAction = usePlaidAccountVerificationStatusStore((state) => state.additionalAction);
  const { isMobile } = useResponsive();
  const messageContent = (
    <Box direction="column">
      <Text color="textTitle" style={{ width: '100%' }}>
        <PreformattedWrapper>
          {processedHtmlText ? (
            <PreFormattedHtmlMessage
              htmlText={processedHtmlText}
              onUploadDocument={onUploadDocument}
              onUploadLmnDocument={onUploadLmnDocument}
              onAppeal={() => setIsAppealOpen(true)}
              hasIcon={!isDefaultStyle && !isInfoStyle && !isInfoSectionStyle}
              onRepay={() => {
                setIsRepaymentShown(true);
              }}
              onRetry={() => {
                setIsChoosingPaymentMethod(true);
              }}
              onVerifyAccount={() => {
                setOnlyOpenPlaidModeId(uniqueId());
              }}
              onCardLink={(_empId?: string, cardId?: string) => {
                push(`${ROUTES.CARD_DETAILS}?card_id=${cardId}`);
              }}
              onReimburseMe={() => {
                window.open(ROUTES.REIMBURSE_ME, '_blank');
              }}
              {...(isUploadButtonEnabled
                ? {}
                : {
                    uploadDocumentButtonProps: { disabled: true, buttonType: 'secondary', rightIcon: null },
                    uploadLmnDocumentButtonProps: { disabled: true, buttonType: 'secondary', rightIcon: null },
                    uploadDocumentRenderWrapper: (button) => (
                      <Hint hintElement={button} hintAlign="right" hintWidth={233}>
                        <Text size="small" textAlign="center">
                          The deadline to submit claims for your plan has passed. Documents can no longer be
                          added.
                        </Text>
                      </Hint>
                    ),
                    uploadLmnDocumentRenderWrapper: (button) => (
                      <Hint hintElement={button} hintAlign="right" hintWidth={233}>
                        <Text size="small" textAlign="center">
                          The deadline to submit claims for your plan has passed. Documents can no longer be
                          added.
                        </Text>
                      </Hint>
                    ),
                  })}
            />
          ) : null}
        </PreformattedWrapper>
      </Text>
    </Box>
  );

  return (
    <Wrapper
      data-testid={`pre_formatted_message${preFormattedData?.preFormattedActivityType}`}
      background="canvas"
      round="regular"
      pad={{
        top: isMobile ? '22px' : (isInfoStyle ? 'spacing16' : 'spacing24'),
        right: 'spacing24',
        bottom: isMobile ? 'spacing24' : (isInfoStyle ? 'spacing16' : 'spacing24'),
        left: 'spacing24',
      }}
      border={{ size: 'small', color: borderColor }}
      {...(isSuccessStyle && !isInfoStyle ? { success: true } : {})}
      {...(isWarningStyle && !isInfoStyle ? { warning: true } : {})}
      {...(isErrorStyle && !isInfoStyle ? { error: true } : {})}
      {...(isInfoSectionStyle && !isInfoStyle ? { info: true } : {})}
    >
      {isSuccessStyle && !isInfoStyle ? (
        <IconWrapper data-testId="success_icon">
          <StatusIcon status={TransactionStatus.Paid} />
        </IconWrapper>
      ) : null}
      {isErrorStyle && !isInfoStyle ? (
        <IconWrapper data-testId="error_icon">
          <StatusIcon status={TransactionStatus.Denied} />
        </IconWrapper>
      ) : null}
      {isWarningStyle && !isInfoStyle ? (
        <IconWrapper data-testId="warning_icon">
          <StatusIcon status={TransactionStatus.NeedsHelp} />
        </IconWrapper>
      ) : null}
      {messageContent}

<ClaimAppealModal isOpen={isAppealOpen} onClose={() => setIsAppealOpen(false)} claimId={data?.claimId} />

      {isRepaymentShown ? (
        <ClaimRepaymentModal
          data={data}
          onClose={() => setIsRepaymentShown(false)}
          isRejectionFlow={claimRepaymentMessageType?.isRepaymentAgain}
        />
      ) : null}

      {isChoosingPaymentMethod ? (
        <ActionRequiredMessage data={data} close={() => setIsChoosingPaymentMethod(false)} />
      ) : null}

      {onlyOpenPlaidModeId ? (
        <PlaidDirectDepositBanner
          status={plaidAccountVerificationStatus}
          accountId={plaidAccountId}
          refetch={additionalAction}
          highlightedTop
          onlyOpenPlaidModeId={onlyOpenPlaidModeId}
        />
      ) : null}
    </Wrapper>
  );
};

export default PreFormattedMessage;
